import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiEvent, ConvertEvent, Event } from '@app/common/models';
import { RecurrenceRule } from '@app/calendar/recurrence-rule';
import { DateTime } from 'luxon';
import { PL_API_CONFIG, PlAPIConfig } from 'pl-core-services';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { EventsChangeNotifier } from '../events-change-notifier';

interface RecurrenceEventParams {
  start: DateTime;
  end?: DateTime;
  recurrenceSetUuid?: string;
}

@Injectable()
export class EventService {
  private eventsUrl;

  constructor(
    private client: HttpClient,
    private notifier: EventsChangeNotifier,
    @Inject(PL_API_CONFIG) config: PlAPIConfig
  ) {
    this.eventsUrl = `${config.kanga}/api/v1/events`;
  }

  async getEvents(options: RecurrenceEventParams): Promise<Event[]> {
    const params: {
      start: string;
      end?: string;
      recurrence_set?: string;
      has_sessions?: boolean;
    } = {
      start: options.start.toISO({ suppressSeconds: true }).replace('+', '%2B'),
    };
    if ('end' in options) {
      params.end = options.end
        .toISO({ suppressSeconds: true })
        .replace('+', '%2B');
    }

    if ('recurrenceSetUuid' in options) {
      params.recurrence_set = options.recurrenceSetUuid;
      params.has_sessions = true;
    }

    const url = `${this.eventsUrl}?start=${params.start}${
      params.end ? `&end=${params.end}` : ''
    }`;
    delete params.start;
    delete params.end;

    const events = await firstValueFrom(
      this.client.get<ApiEvent[]>(url, { params })
    );
    return events.map(ConvertEvent.fromApi);
  }

  async update(
    event: Event,
    options?: { thisAndFollowing?: boolean }
  ): Promise<void> {
    const params: { [n: string]: string } = {};
    if (options?.thisAndFollowing) {
      params['this_and_following'] = 'yes';
    }
    const body = ConvertEvent.toApi(event);
    await firstValueFrom(
      this.client.put<void>(`${this.eventsUrl}/${event.id}/`, body, {
        params,
      })
    );
    this.notifier.notifyEventsChanged();
  }

  delete(uuid: string, following?: boolean): Observable<any> {
    let params = {};
    if (following) {
      params = { this_and_following: 'yes' };
    }
    return this.client
      .delete(`${this.eventsUrl}/${uuid}/`, { params })
      .pipe(tap(() => this.notifier.notifyEventsChanged()));
  }

  async hasEvents(): Promise<boolean> {
    const response = await firstValueFrom(
      this.client.get<{ count: number } | ApiEvent[]>(this.eventsUrl, {
        params: { limit: '1' },
      })
    );
    if (Array.isArray(response)) {
      return response.length > 0;
    }
    return (response?.count ?? 0) > 0;
  }

  async previewRecurrenceRuleChange(
    eventId: string,
    recurrenceRule: RecurrenceRule,
    startDate: DateTime
  ): Promise<{ event: Event; newDate: DateTime }[]> {
    const url = `${this.eventsUrl}/${eventId}/preview_recurrence_rule_change`;
    const list = await firstValueFrom(
      this.client.post<{ event: ApiEvent; new_date: string }[]>(url, {
        recurrence_rule: recurrenceRule.toString(),
        start_date: startDate.toISODate(),
      })
    );
    return list.map(({ event, new_date }) => ({
      event: ConvertEvent.fromApi(event),
      newDate: DateTime.fromISO(new_date),
    }));
  }
}
